/**
 * Contains types for RoofSystems. These are consumed by generateBuilding to actually make the building.
 * @module
 */
import type { ComponentTransform } from "building-types/components/componentTypes";
import type { InstanceScopedNamespacePath } from "building-types/components/instanceScopedNamespacePathTypes";
import type { DocEntityParentId } from "building-types/doc/docEntityConstraintTypes";
import type { DocForeignKeyTable } from "building-types/doc/docForeignKeyTable";
import type { PrimaryEntityId } from "building-types/doc/primaryEntityId";
import type { FormulaDisabledDocEntityBase } from "building-types/entities/docEntityBase";
import type { LabelableEntityBase } from "building-types/entities/labelTypes";
import type { Label } from "building-types/facades/label";
import type { Awning } from "building-types/openings/awning";
import type { Formula } from "building-types/options/formula";
import type { ProfileId } from "building-types/profiles/profileTypes";
import type { DormerSettings } from "building-types/settings/dormerSettings";
import type {
  DormerPlanSettings,
  DormerElevationPlanSettings,
  RoofPlanSettings,
  RoofElevationPlanSettings,
} from "building-types/settings/roofPlanSettings";
import type { RoofSettings } from "building-types/settings/roofSettings";
import type {
  BuildingSide,
  RoofFaces,
  RoofSideOverridesTable,
  RoofEdgeOverridesTable,
  RoofSide,
  EvaluatedRoofEdgeOverridesTable,
  EvaluatedRoofSideOverridesTable,
} from "building-types/settings/roofTypes";
import type { WallRoofBaseBoardMode, WallRoofBaseBoardFilter } from "building-types/settings/settingsTypesEnum";
import { EaveFasciaHeightDatum, RoofPeakDirection } from "building-types/settings/settingsTypesEnum";
import type { BuiltinWindowId } from "building-types/windows/windowId";
import type { WindowSettingsBase } from "building-types/windows/windowSettingsBaseTypes";
import type { Table } from "common/data/table";
import type { PolyLine2 } from "geom/polyLine2Types";
import type { ForeignEntityId } from "../handles/foreignEntityId";

export const CURRENT_GABLE_ROOF_VERSION = 5;

export interface RoofDormerPlacement extends FormulaDisabledDocEntityBase<"DORMER">, WindowSettingsBase {
  readonly id: PrimaryEntityId<"DORMER">;
  readonly name: string;
  readonly windowVerticalOffset?: number;
  readonly gableOpeningPlacement?: RoofOpeningPlacement;
  readonly windowType?: ForeignEntityId<"WINDOW_DESCRIPTOR"> | BuiltinWindowId;
  readonly awning?: Awning;
  readonly useNewPlacementMethod?: boolean;
  readonly dormerPlanSettings?: Partial<DormerPlanSettings>;
  readonly dormerSettings?: Partial<DormerSettings>;
  readonly dormerElevationPlanSettings?: Partial<DormerElevationPlanSettings>;
  readonly parentId?: DocEntityParentId<"ROOF">;
}

export const eaveFasciaHeightDatumRenamer: { [key in EaveFasciaHeightDatum]: string } = {
  [EaveFasciaHeightDatum.DEFAULT]: "T.O. Wall Plate",
  [EaveFasciaHeightDatum.HOST_ROOF]: "T.O. Host Roof",
  [EaveFasciaHeightDatum.HOST_ROOF_START_TRUNCATION]: "T.O. Host Roof Start Truncation",
  [EaveFasciaHeightDatum.HOST_ROOF_END_TRUNCATION]: "T.O. Host Roof End Truncation",
};

export interface GeometryRef {
  readonly type: "GEOMETRY_REF";
  readonly geomId: string;
}

export interface RoofSystem extends FormulaDisabledDocEntityBase<"ROOF_SYSTEM"> {
  readonly id: PrimaryEntityId<"ROOF_SYSTEM">;
}

export interface RoofSystemInternal {
  readonly name: string;
  readonly id: string;
  readonly subRoofs: DocForeignKeyTable<"ROOF", EvaluatedRoof>;
}

export type RoofSystemTableInternal = Table<RoofSystemInternal>;

// TODO: If we use InstanceScopedNamespacePath<"LEVEL"> directly at VirtualRoofBlock,
// genDocSchema will create an untyped foreign key ("entityId: fk(),"), and typecheck will fail.
// We need to update genDocSchema to support this type definition.
export interface RoofSystemLevelUsage extends InstanceScopedNamespacePath<"LEVEL"> {}

export interface VirtualRoofBlock {
  readonly type: "VIRTUAL_ROOF_BLOCK";
  readonly name: string;
  readonly perimeter: PolyLine2;
  readonly edgeIds: string[];
  readonly levelUsage: RoofSystemLevelUsage;
}

export interface GableLabel extends Label {
  face: RoofFaces;
}

/**
 * Instance-scoped block reference for non-virtual roof blocks.
 * Inherits the FK deletion-guard gap from InstanceScopedNamespacePath — see
 * the note on that interface and the matching comment on RoofSystemLevelUsage.
 */
export interface RoofBlockInstanceRef extends InstanceScopedNamespacePath<"BLOCK"> {}

export type RoofBlocks =
  | ForeignEntityId<"BLOCK">
  | ForeignEntityId<"BLOCK">[]
  | RoofBlockInstanceRef
  | RoofBlockInstanceRef[]
  | VirtualRoofBlock;

export enum RoofFlags {
  LEGACY_TRUNCATION = "LEGACY_TRUNCATION",
  LEGACY_HEEL_HEIGHT_OFFSETS = "LEGACY_HEEL_HEIGHT_OFFSETS",
  LEGACY_SLOPE = "LEGACY_SLOPE",
  LEGACY_EAVE_RETURNS = "LEGACY_EAVE_RETURNS",
}

export type RoofFlagsTable = { readonly [key in RoofFlags]?: boolean };

/**
 * Extracting all the specific data from Roof, so we can pass it directly
 * to makeGableSubRoof without needing a full Roof entity. This is needed
 * because dormer roofs are generated from a different kind of entity.
 *
 * We are making the assumption that the shape of a single block perimeter or block
 * group perimeter of a GableRoofSystem is always a rectangle.
 * In other words, the single block perimeter or block group perimeter should be a
 * polyline2 with a length of 10 numbers -> 5 vertices -> 4 edges.
 */
export interface RoofData {
  readonly parentId: DocEntityParentId<"ROOF_SYSTEM">;
  readonly roofVersion: number;
  readonly blocks: RoofBlocks;
  readonly lowRailIndexForPeakDirection?: number;
  readonly gableEndLabels?: GableLabel[];
  readonly wallRoofBaseBoard?: WallRoofBaseBoardMode;
  readonly wallRoofBaseBoardFilter?: WallRoofBaseBoardFilter;
  readonly wallRoofBaseBoardProfile?: ProfileId;
  readonly suppressWallExtension?: boolean;
  readonly parentNeighbors?: string[];
  readonly flattenLeftSideSlopes?: boolean;
  readonly flattenRightSideSlopes?: boolean;
  readonly roofSideOverrides: RoofSideOverridesTable;
  readonly roofEdgeOverrides: RoofEdgeOverridesTable; // will only be used by clip to perimeter roofs
  readonly flags?: RoofFlagsTable;
  readonly roofSettings?: Partial<RoofSettings>;
  readonly dormerSettings?: Partial<DormerSettings>;
  readonly roofPlanSettings?: Partial<RoofPlanSettings>;
  readonly roofElevationPlanSettings?: Partial<RoofElevationPlanSettings>;
}

/**
 * RoofData without the Formula fields on RoofData
 */
export interface EvaluatedRoofData extends RoofData {
  readonly roofSideOverrides: EvaluatedRoofSideOverridesTable;
  readonly roofEdgeOverrides: EvaluatedRoofEdgeOverridesTable;
  /** Generation-only hint for component instance roof copies (not persisted on doc). */
  readonly componentInstanceChain?: readonly string[];
  readonly componentTransform?: ComponentTransform;
}

/**
 * RoofData without the Formula fields on RoofData
 */
export interface EvaluatedRoof extends Roof {
  readonly roofSideOverrides: EvaluatedRoofSideOverridesTable;
  readonly roofEdgeOverrides: EvaluatedRoofEdgeOverridesTable;
  /** Generation-only hint for component instance roof copies (not persisted on doc). */
  readonly componentInstanceChain?: readonly string[];
  readonly componentTransform?: ComponentTransform;
}

/**
 * Roof entity with RoofData fields
 */
export interface Roof extends FormulaDisabledDocEntityBase<"ROOF">, RoofData, LabelableEntityBase {
  readonly id: PrimaryEntityId<"ROOF">;
  readonly parentId: DocEntityParentId<"ROOF_SYSTEM">;
}

export enum RoofOpeningJustification {
  BASE_UP = "BASE_UP",
  PEAK_DOWN = "PEAK_DOWN",
}

export interface RoofOpeningPlacement extends FormulaDisabledDocEntityBase<"ROOF_OPENING">, WindowSettingsBase {
  readonly id: PrimaryEntityId<"ROOF_OPENING">;
  readonly parentId?: DocEntityParentId<"ROOF">;
  readonly name: string;
  readonly windowType: ForeignEntityId<"WINDOW_DESCRIPTOR"> | BuiltinWindowId;
  readonly justification?: RoofOpeningJustification;
  readonly offset?: number | Formula;
  readonly horizontalOffset?: number;
  readonly awning?: Awning;
  readonly useNewPlacementMethod?: boolean;
  readonly side?: RoofSide; //roof side at which the opening is placed
  readonly useLegacyAttachmentSettings?: boolean;
}

export interface SkylightPlacement extends FormulaDisabledDocEntityBase<"SKYLIGHT"> {
  readonly id: PrimaryEntityId<"SKYLIGHT">;
  readonly parentId?: DocEntityParentId<"ROOF">;
  readonly name: string;
  readonly side: BuildingSide;
  readonly horizontalPositions?: number[] | null;
  readonly normalizedPositions?: boolean;
  readonly verticalPosition?: number;
  readonly width?: number;
  readonly height?: number;
  readonly useNewPlacementMethod?: boolean;
}

export const isIndexedRoofPeakDirection: { [k in RoofPeakDirection]?: boolean } = {
  [RoofPeakDirection.BY_LOW_RAIL_INDEX]: true,
  [RoofPeakDirection.BY_GABLE_END_RAIL_INDEX]: true,
};
